import { supabase } from '../lib/supabase';

/**
 * Real-time Notification Service
 * Manages Supabase real-time subscriptions for instant notifications
 * across contracts, clients, and staff alerts
 */
class RealtimeNotificationService {
  constructor() {
    this.subscriptions = new Map();
    this.listeners = new Map();
  }

  /**
   * Subscribe to contract changes (new, updated, approved)
   * @param {Function} callback - Called when contract changes occur
   * @returns {Function} Unsubscribe function
   */
  subscribeToContracts(callback) {
    if (!supabase) {
      console.warn('Supabase not initialized. Real-time features disabled.');
      return () => {};
    }

    const channelName = 'contracts-realtime';
    
    // Remove existing subscription if any
    this.unsubscribe(channelName);

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'contracts'
        },
        (payload) => {
          const { eventType, new: newRecord, old: oldRecord } = payload;
          
          callback({
            type: 'contract',
            event: eventType,
            data: newRecord || oldRecord,
            timestamp: new Date()?.toISOString()
          });
        }
      )?.subscribe();

    this.subscriptions?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  /**
   * Subscribe to marketing contract changes
   * @param {Function} callback - Called when marketing contract changes occur
   * @returns {Function} Unsubscribe function
   */
  subscribeToMarketingContracts(callback) {
    if (!supabase) {
      console.warn('Supabase not initialized. Real-time features disabled.');
      return () => {};
    }

    const channelName = 'marketing-contracts-realtime';
    
    this.unsubscribe(channelName);

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'marketing_contracts'
        },
        (payload) => {
          const { eventType, new: newRecord, old: oldRecord } = payload;
          
          callback({
            type: 'marketing_contract',
            event: eventType,
            data: newRecord || oldRecord,
            timestamp: new Date()?.toISOString()
          });
        }
      )?.subscribe();

    this.subscriptions?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  /**
   * Subscribe to buyer representation contract changes
   * @param {Function} callback - Called when buyer contract changes occur
   * @returns {Function} Unsubscribe function
   */
  subscribeToBuyerContracts(callback) {
    if (!supabase) {
      console.warn('Supabase not initialized. Real-time features disabled.');
      return () => {};
    }

    const channelName = 'buyer-contracts-realtime';
    
    this.unsubscribe(channelName);

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'buyer_representation_contracts'
        },
        (payload) => {
          const { eventType, new: newRecord, old: oldRecord } = payload;
          
          callback({
            type: 'buyer_contract',
            event: eventType,
            data: newRecord || oldRecord,
            timestamp: new Date()?.toISOString()
          });
        }
      )?.subscribe();

    this.subscriptions?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  /**
   * Subscribe to client changes (new, updated)
   * @param {Function} callback - Called when client changes occur
   * @returns {Function} Unsubscribe function
   */
  subscribeToClients(callback) {
    if (!supabase) {
      console.warn('Supabase not initialized. Real-time features disabled.');
      return () => {};
    }

    const channelName = 'clients-realtime';
    
    this.unsubscribe(channelName);

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'clients'
        },
        (payload) => {
          const { eventType, new: newRecord, old: oldRecord } = payload;
          
          callback({
            type: 'client',
            event: eventType,
            data: newRecord || oldRecord,
            timestamp: new Date()?.toISOString()
          });
        }
      )?.subscribe();

    this.subscriptions?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  /**
   * Subscribe to staff alert changes
   * @param {Function} callback - Called when alert changes occur
   * @returns {Function} Unsubscribe function
   */
  subscribeToAlerts(callback) {
    if (!supabase) {
      console.warn('Supabase not initialized. Real-time features disabled.');
      return () => {};
    }

    const channelName = 'alerts-realtime';
    
    this.unsubscribe(channelName);

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'alerts'
        },
        (payload) => {
          const { eventType, new: newRecord, old: oldRecord } = payload;
          
          callback({
            type: 'alert',
            event: eventType,
            data: newRecord || oldRecord,
            timestamp: new Date()?.toISOString()
          });
        }
      )?.subscribe();

    this.subscriptions?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  /**
   * Subscribe to client recommendation changes
   * @param {Function} callback - Called when recommendation changes occur
   * @returns {Function} Unsubscribe function
   */
  subscribeToRecommendations(callback) {
    if (!supabase) {
      console.warn('Supabase not initialized. Real-time features disabled.');
      return () => {};
    }

    const channelName = 'recommendations-realtime';
    
    this.unsubscribe(channelName);

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'client_recommendations'
        },
        (payload) => {
          const { eventType, new: newRecord, old: oldRecord } = payload;
          
          callback({
            type: 'recommendation',
            event: eventType,
            data: newRecord || oldRecord,
            timestamp: new Date()?.toISOString()
          });
        }
      )?.subscribe();

    this.subscriptions?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  /**
   * Subscribe to contract approval changes
   * @param {Function} callback - Called when approval changes occur
   * @returns {Function} Unsubscribe function
   */
  subscribeToContractApprovals(callback) {
    if (!supabase) {
      console.warn('Supabase not initialized. Real-time features disabled.');
      return () => {};
    }

    const channelName = 'contract-approvals-realtime';
    
    this.unsubscribe(channelName);

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'contract_approvals'
        },
        (payload) => {
          const { eventType, new: newRecord, old: oldRecord } = payload;
          
          callback({
            type: 'contract_approval',
            event: eventType,
            data: newRecord || oldRecord,
            timestamp: new Date()?.toISOString()
          });
        }
      )?.subscribe();

    this.subscriptions?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  /**
   * Subscribe to workflow job changes
   * @param {Function} callback - Called when workflow job changes occur
   * @returns {Function} Unsubscribe function
   */
  subscribeToWorkflowJobs(callback) {
    if (!supabase) {
      console.warn('Supabase not initialized. Real-time features disabled.');
      return () => {};
    }

    const channelName = 'workflow-jobs-realtime';
    
    this.unsubscribe(channelName);

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'workflow_jobs'
        },
        (payload) => {
          const { eventType, new: newRecord, old: oldRecord } = payload;
          
          callback({
            type: 'workflow_job',
            event: eventType,
            data: newRecord || oldRecord,
            timestamp: new Date()?.toISOString()
          });
        }
      )?.subscribe();

    this.subscriptions?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  /**
   * Unsubscribe from a specific channel
   * @param {string} channelName - Name of the channel to unsubscribe
   */
  unsubscribe(channelName) {
    const channel = this.subscriptions?.get(channelName);
    if (channel) {
      supabase?.removeChannel(channel);
      this.subscriptions?.delete(channelName);
    }
  }

  /**
   * Unsubscribe from all channels
   */
  unsubscribeAll() {
    this.subscriptions?.forEach((channel, channelName) => {
      supabase?.removeChannel(channel);
    });
    this.subscriptions?.clear();
  }

  /**
   * Get active subscription count
   * @returns {number} Number of active subscriptions
   */
  getActiveSubscriptionCount() {
    return this.subscriptions?.size;
  }
}

// Export singleton instance
export const realtimeNotificationService = new RealtimeNotificationService();
export default realtimeNotificationService;
